import type { DashboardDefinition, DashboardResolveWidgetLine } from "@t3tools/contracts";
import {
  BadgeCheckIcon,
  ClockAlertIcon,
  DatabaseIcon,
  FlaskConicalIcon,
  HourglassIcon,
  InfoIcon,
  TriangleAlertIcon,
} from "lucide-react";
import {
  createContext,
  useContext,
  useId,
  useState,
  type FocusEvent,
  type ReactElement,
  type ReactNode,
} from "react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Popover, PopoverPopup, PopoverTrigger } from "~/components/ui/popover";
import { cn } from "~/lib/utils";

import {
  artifactTrustDescription,
  formatCheckedAt,
  includedDashboardLabels,
  type ArtifactTrust,
  type ArtifactTrustLevel,
} from "./artifactTrust";
import type { ArtifactDataset } from "./artifactTypes";

/**
 * The trust popover: on every KPI value and chart point, what the number is,
 * whether it is Verified, how fresh it is and whether it may still change.
 * It replaces "How is this calculated?": ad-hoc widgets also show their query.
 */

/** A chart point the popover describes; KPIs describe their single value. */
export type ArtifactTrustPoint = {
  readonly x: string;
  readonly series: string;
  readonly value: string;
  readonly settling: boolean;
  readonly suppressed: boolean;
};

/** What a later action needs to act on the number the popover is open for. */
export type ArtifactTrustSlotContext = {
  readonly widgetId: string;
  readonly title: string;
  readonly line: DashboardResolveWidgetLine;
  readonly point: ArtifactTrustPoint | null;
};

type Slot = (context: ArtifactTrustSlotContext) => ReactNode;

/**
 * Actions that later work fills in: "Why did this change?" (CM-2790), "See
 * rows" (CM-2794) and the reported-vs-now line (CM-2782). A slot that renders
 * nothing leaves no gap.
 */
export type ArtifactTrustSlots = {
  readonly reportedVsNow?: Slot;
  readonly whyDidThisChange?: Slot;
  readonly seeRows?: Slot;
};

const ArtifactTrustSlotsContext = createContext<ArtifactTrustSlots>({});

export function ArtifactTrustSlotsProvider(props: {
  children: ReactNode;
  slots: ArtifactTrustSlots;
}) {
  return (
    <ArtifactTrustSlotsContext.Provider value={props.slots}>
      {props.children}
    </ArtifactTrustSlotsContext.Provider>
  );
}

const LEVEL_BADGE: Record<
  ArtifactTrustLevel,
  { variant: "success" | "outline" | "warning" | "error"; icon: ReactElement }
> = {
  verified: { variant: "success", icon: <BadgeCheckIcon aria-hidden /> },
  ad_hoc: { variant: "outline", icon: <FlaskConicalIcon aria-hidden /> },
  unverified: { variant: "outline", icon: <InfoIcon aria-hidden /> },
  raw: { variant: "warning", icon: <DatabaseIcon aria-hidden /> },
  failing: { variant: "error", icon: <TriangleAlertIcon aria-hidden /> },
};

export function ArtifactTrustBadge(props: { level: ArtifactTrustLevel; label: string }) {
  const badge = LEVEL_BADGE[props.level];
  return (
    <Badge variant={badge.variant} data-artifact-trust={props.level}>
      {badge.icon}
      {props.label}
    </Badge>
  );
}

export function ArtifactSettlingChip(props: { reason?: string | undefined }) {
  return (
    <Badge variant="info" size="sm" title={props.reason} data-artifact-settling="">
      <HourglassIcon aria-hidden />
      Settling
    </Badge>
  );
}

function cadenceText(minutes: number): string {
  if (minutes % (24 * 60) === 0) {
    const days = minutes / (24 * 60);
    return days === 1 ? "daily" : `every ${days} days`;
  }
  if (minutes % 60 === 0) return minutes === 60 ? "hourly" : `every ${minutes / 60} hours`;
  return `every ${minutes} minutes`;
}

/**
 * "Updated 6 min ago" and a Settling chip, quietly under every widget. Stale
 * numbers turn amber. Renders inline content so it fits a KPI footnote.
 */
export function ArtifactWidgetFreshness(props: { trust: ArtifactTrust | null }) {
  const { trust } = props;
  if (!trust?.freshness && !trust?.settling) return null;
  return (
    <span className="mt-1 flex flex-wrap items-center gap-1.5" data-artifact-freshness="">
      {trust.freshness ? (
        <span
          className={cn(
            "inline-flex items-center gap-1",
            trust.freshness.stale ? "font-medium text-warning-foreground" : "text-muted-foreground",
          )}
          data-stale={trust.freshness.stale || undefined}
        >
          {trust.freshness.stale ? <ClockAlertIcon className="size-3" aria-hidden /> : null}
          {trust.freshness.text}
        </span>
      ) : null}
      {trust.settling ? <ArtifactSettlingChip reason={trust.settling.reason} /> : null}
    </span>
  );
}

function QueryDetails(props: { dataset: ArtifactDataset }) {
  const { dataset } = props;
  return (
    <details className="group text-xs">
      <summary className="cursor-pointer font-medium text-muted-foreground hover:text-foreground">
        Show the query
      </summary>
      <div className="mt-2 space-y-1.5">
        {includedDashboardLabels(dataset.sql).map((label) => (
          <p key={label} className="text-muted-foreground">
            Built on the {label} dashboard's own query rather than a new definition.
          </p>
        ))}
        {dataset.bind.length ? (
          <p className="text-muted-foreground">
            Filters bound in order:{" "}
            {dataset.bind.map((ref, index) => (
              <span key={ref}>
                {index ? ", " : ""}
                <code>${index + 1}</code> = <code>{ref}</code>
              </span>
            ))}
          </p>
        ) : null}
        <pre className="max-h-48 overflow-auto rounded-md border border-border/70 bg-muted/40 p-2 font-mono text-[11px] leading-relaxed text-foreground">
          {dataset.sql}
        </pre>
      </div>
    </details>
  );
}

export type ArtifactTrustDetailsProps = {
  /** Written definitions the widget cites (dataset widgets only). */
  definitions: ReadonlyArray<DashboardDefinition>;
  /** The ad-hoc dataset behind the widget, whose query the popover can show. */
  dataset: ArtifactDataset | undefined;
  line: DashboardResolveWidgetLine;
  point: ArtifactTrustPoint | null;
  title: string;
  trust: ArtifactTrust;
  /** The KPI's formatted value; chart points carry their own. */
  value?: string | undefined;
  widgetId: string;
};

/** The popover's content. */
export function ArtifactTrustDetails(props: ArtifactTrustDetailsProps) {
  const { trust, point } = props;
  const slots = useContext(ArtifactTrustSlotsContext);
  const slotContext: ArtifactTrustSlotContext = {
    widgetId: props.widgetId,
    title: props.title,
    line: props.line,
    point,
  };
  const settling = trust.settling && (!point || point.settling) ? trust.settling : null;
  const actions = [slots.whyDidThisChange?.(slotContext), slots.seeRows?.(slotContext)].filter(
    Boolean,
  );
  const reportedVsNow = slots.reportedVsNow?.(slotContext);

  return (
    <div
      className="flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-3 text-sm"
      data-artifact-trust-details=""
    >
      <div className="space-y-0.5">
        <p className="font-semibold text-foreground">{props.title}</p>
        {point ? (
          <p className="text-xs text-muted-foreground">
            {point.x} · {point.series}:{" "}
            <span className="font-medium text-foreground">
              {point.suppressed ? "hidden" : point.value}
            </span>
          </p>
        ) : props.value ? (
          <p className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{props.value}</span>
          </p>
        ) : null}
        {point?.suppressed ? (
          <p className="text-xs text-muted-foreground">
            Hidden because a count under 11 could identify members.
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <ArtifactTrustBadge level={trust.level} label={trust.label} />
        {trust.sources.map((source, index) => (
          <div key={`${source.metricId ?? source.datasetId ?? "source"}-${index}`}>
            {trust.sources.length > 1 || source.title !== props.title ? (
              <p className="text-xs font-medium text-foreground">{source.title}</p>
            ) : null}
            <p className="text-xs leading-relaxed text-muted-foreground">{source.definition}</p>
            {source.detail ? (
              <p className="mt-0.5 text-xs text-muted-foreground">{source.detail}</p>
            ) : null}
          </div>
        ))}
        {props.definitions.length ? (
          <dl className="space-y-1.5">
            {props.definitions.map((definition) => (
              <div key={definition.id}>
                <dt className="text-xs font-medium text-foreground">{definition.term}</dt>
                <dd className="text-xs leading-relaxed text-muted-foreground">{definition.text}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </div>

      {trust.freshness ? (
        <div
          className={cn(
            "flex items-start gap-1.5 text-xs",
            trust.freshness.stale ? "text-warning-foreground" : "text-muted-foreground",
          )}
          data-stale={trust.freshness.stale || undefined}
        >
          {trust.freshness.stale ? (
            <ClockAlertIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          ) : null}
          <p>
            {trust.freshness.text}
            <span className="block text-muted-foreground">
              {trust.freshness.stale
                ? `Stale: the source usually updates ${cadenceText(trust.freshness.cadenceMinutes)}, and this run is ${formatCheckedAt(trust.freshness.computedAt)}.`
                : `Source last ran ${formatCheckedAt(trust.freshness.computedAt)}.`}
            </span>
          </p>
        </div>
      ) : null}

      {settling ? (
        <div className="flex items-start gap-2 text-xs">
          <ArtifactSettlingChip />
          <p className="text-muted-foreground">{settling.reason}</p>
        </div>
      ) : null}

      {reportedVsNow ? <div className="text-xs">{reportedVsNow}</div> : null}
      {props.dataset ? <QueryDetails dataset={props.dataset} /> : null}
      {actions.length ? (
        <div className="flex flex-wrap gap-2 border-t border-border/70 pt-2">{actions}</div>
      ) : null}
    </div>
  );
}

/**
 * Wraps a number in the trust popover. It opens on hover, on tap or click,
 * and when the number gets keyboard focus; its trust summary is the number's
 * aria description either way.
 */
export function ArtifactTrustPopover(
  props: ArtifactTrustDetailsProps & {
    children: ReactNode;
    className?: string;
    /** Names the trigger, e.g. "CN billed completion: 90.9%". */
    label: string;
  },
) {
  const { children, className, label, ...details } = props;
  const [open, setOpen] = useState(false);
  const descriptionId = useId();
  const openOnKeyboardFocus = (event: FocusEvent<HTMLElement>) => {
    if (event.currentTarget.matches(":focus-visible")) setOpen(true);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        openOnHover
        delay={250}
        aria-label={label}
        aria-describedby={descriptionId}
        onFocus={openOnKeyboardFocus}
        data-artifact-trust-trigger=""
        className={cn(
          "cursor-help rounded-sm text-left underline decoration-muted-foreground/30 decoration-dotted underline-offset-4 outline-none hover:decoration-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
          className,
        )}
      >
        {children}
        <span id={descriptionId} className="sr-only">
          {artifactTrustDescription(details.trust)}
        </span>
      </PopoverTrigger>
      <PopoverPopup side="bottom" align="start" initialFocus={false}>
        <ArtifactTrustDetails {...details} />
      </PopoverPopup>
    </Popover>
  );
}

/** An "About this data" button for tables and chart headers, with the same popover. */
export function ArtifactTrustButton(props: ArtifactTrustDetailsProps) {
  const [open, setOpen] = useState(false);
  const descriptionId = useId();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            size="icon-xs"
            variant="ghost"
            aria-label={`About ${props.title}`}
            aria-describedby={descriptionId}
          />
        }
        data-dashboard-pdf-export=""
      >
        <InfoIcon aria-hidden />
        <span id={descriptionId} className="sr-only">
          {artifactTrustDescription(props.trust)}
        </span>
      </PopoverTrigger>
      <PopoverPopup side="bottom" align="end">
        <ArtifactTrustDetails {...props} />
      </PopoverPopup>
    </Popover>
  );
}

/**
 * The popover for one chart point, anchored where the point is drawn. The
 * chart opens it on click or tap, and moves it with the arrow keys.
 */
export function ArtifactChartPointPopover(
  props: Omit<ArtifactTrustDetailsProps, "point"> & {
    anchor: { x: number; y: number; element: HTMLElement } | null;
    onClose: () => void;
    point: ArtifactTrustPoint | null;
  },
) {
  const { anchor, onClose, ...details } = props;
  const open = anchor !== null && details.point !== null;
  const virtualAnchor = anchor
    ? {
        getBoundingClientRect: () => {
          const rect = anchor.element.getBoundingClientRect();
          return new DOMRect(rect.left + anchor.x, rect.top + anchor.y, 0, 0);
        },
      }
    : null;
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      {open && virtualAnchor ? (
        <PopoverPopup side="top" anchor={virtualAnchor} initialFocus={false} finalFocus={false}>
          <ArtifactTrustDetails {...details} />
        </PopoverPopup>
      ) : null}
    </Popover>
  );
}
